import { useTranslation } from 'react-i18next'
import { PhoneOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTabStateStore } from '@/stores/useTabStateStore'

/**
 * Overlay affiché quand cet onglet a été désactivé par un autre onglet du
 * même utilisateur (1 device Twilio = 1 tab actif).
 *
 * L'auth reste valide — on n'a pas wipé les tokens. Cliquer "Reprendre ici"
 * reload la page : le hook lifecycle re-broadcast un claim avec un ts plus
 * récent, l'autre tab passe à son tour en inactive, et celui-ci redevient
 * l'onglet actif avec un Device Twilio fonctionnel.
 */
export function InactiveTabOverlay() {
  const { t } = useTranslation()
  const isActive = useTabStateStore((s) => s.isActive)
  // The tab holding the phone is on a call: it kept the phone, this one stood down.
  const appelAilleurs = useTabStateStore((s) => s.raison === 'appel_ailleurs')

  if (isActive) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="max-w-md mx-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl p-8 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30 mb-4">
          <PhoneOff className="h-7 w-7 text-amber-600 dark:text-amber-400" />
        </div>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
          {appelAilleurs
            ? t('tabTakeover.busyTitle', 'Un appel est en cours dans un autre onglet')
            : t(
                'tabTakeover.title',
                'Onglet inactif',
              )}
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
          {appelAilleurs
            ? t(
                'tabTakeover.busyDescription',
                "Le téléphone reste dans l'onglet de l'appel. Revenez-y, ou cliquez sur « Reprendre ici » une fois l'appel terminé.",
              )
            : t(
                'tabTakeover.description',
                'Un autre onglet Callizy a pris le contrôle du téléphone. Vous ne pouvez utiliser le téléphone que dans un onglet à la fois.',
              )}
        </p>
        <Button
          onClick={() => window.location.reload()}
          className="w-full bg-[#0A192F] hover:bg-[#0A192F]/90 text-white dark:bg-white dark:text-[#0A192F] dark:hover:bg-white/90"
        >
          {t('tabTakeover.resumeHere', 'Reprendre ici')}
        </Button>
      </div>
    </div>
  )
}
